import React, { useEffect, useState } from 'react';
import Toast from "../../../components/Toast";
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { useAuth } from '../../../AuthContext';
import SideNavbar from '../../../components/SideNavbar';
import { useAppSelector } from '../../../redux/store';

// additonal imports
import { Plus } from "lucide-react";

// modals
import SettingsModal from '../../../components/modals/SettingsModal';

interface LeadAdmissionStatus {
  admissionStatus: string;
  level: number;
}

interface LeadSubAdmissionStatus {
  admissionStatusId: string;
  subAdmissionStatus: string;
}

interface LeadSource {
  source: string;
}

interface LeadSubSource {
  sourceId: string;
  subSource: string;
}

interface LeadStage {
  stage: string;
  priority: number;
  color: string;
}

interface LeadSubStage {
  stageId: string;
  subStage: string;
}

interface LeadSubStatus {
  statusId: string;
  subStatus: string;
}

interface LeadState {
  state: string;
  makeInactive: boolean;
}

interface LeadStatus {
  status: string;
  priority: number;
  color: string;
}

interface LeadWelcomeCallByAdmissionTeamStatus {
  welcomeCallByAdmissionTeamStatus: string;
}

interface LeadVisaFileProcessingStatus {
  visaFileProcessingStatus: string;
}

interface LeadApplicationMovedStatus {
  applicationMovedStatus: string;
}

interface LeadOfferLetterStatus {
  offerLetterStatus: string;
}

interface LeadFeePaymentStatus {
  feePaymentStatus: string;
}

interface LeadFileSubmissionStatus {
  fileSubmissionStatus: string;
}

interface RegistrationFeeDetails {
  registrationFeeName: string;
  registrationFeeAmount: string;
}

interface LeadCampaigns {
  campaign: string;
  isActive: boolean;
}

interface PrefferedStreams {
  preferredStream: string;
}

interface ProgramTypes {
  programType: string;
}

interface Department {
  department: string;
  selectedFilterIds?: string[];
}

interface FilterSetting {
  title: string;
  description?: string;
}

interface DocumentWithId {
  id: string;
  data: any;
  sub?: any;
}

interface SuperAgent {
  superAgent: string;
}

interface Pipeline {
  pipelineName: string;
  pipelineDescription: string;
}

interface DocumentCategory {
  categoryName: string;
  documentValues: string[];
}

interface FilterButton {
  name: string;
  description: string;
  color?: string;
}

interface Language {
  language: string;
}

const Settings = () => {
  const { userData } = useAuth();
  const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // modal states
  const [showModal, setShowModal] = useState(false);
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);

  const [title, setTitle] = useState('');
  const [action, setAction] = useState('');
  const [data, setData] = useState<DocumentWithId[]>([]);

  const [admissionStatuses, setAdmissionStatuses] = useState<DocumentWithId[]>([]);
  const [leadSubAdmissionStatuses, setLeadSubAdmissionStatuses] = useState<DocumentWithId[]>([]);
  const [selectedAdmissionStatuses, setSelectedAdmissionStatuses] = useState<string>('');
  const [leadSources, setLeadSources] = useState<DocumentWithId[]>([]);
  const [leadSubSources, setLeadSubSources] = useState<DocumentWithId[]>([]);
  const [selectedLeadSources, setSelectedLeadSources] = useState<string>('');
  const [leadStages, setLeadStages] = useState<DocumentWithId[]>([]);
  const [leadSubStages, setLeadSubStages] = useState<DocumentWithId[]>([]);
  const [leadSubStatuses, setLeadSubStatuses] = useState<DocumentWithId[]>([]);
  const [selectedLeadStages, setSelectedLeadStages] = useState<string>('');
  const [leadStates, setLeadStates] = useState<DocumentWithId[]>([]);
  const [selectedLeadStates, setSelectedLeadStates] = useState<string>('');
  const [leadStatuses, setLeadStatuses] = useState<DocumentWithId[]>([]);
  const [selectedLeadStatuses, setSelectedLeadStatuses] = useState<string>('');
  const [welcomeCallByAdmissionTeamStatuses, setWelcomeCallByAdmissionTeamStatuses] = useState<DocumentWithId[]>([]);
  const [selectedWelcomeCallByAdmissionTeamStatuses, setSelectedWelcomeCallByAdmissionTeamStatuses] = useState<string>('');
  const [visaProcessingStatuses, setVisaProcessingStatuses] = useState<DocumentWithId[]>([]);
  const [selectedVisaProcessingStatuses, setSelectedVisaProcessingStatuses] = useState<string>('');
  const [applicationMovedStatuses, setApplicationMovedStatuses] = useState<DocumentWithId[]>([]);
  const [selectedApplicationMovedStatuses, setSelectedApplicationMovedStatuses] = useState<string>('');
  const [offerLetterStatuses, setOfferLetterStatuses] = useState<DocumentWithId[]>([]);
  const [selectedOfferLetterStatuses, setSelectedOfferLetterStatuses] = useState<string>('');
  const [feePaymentStatuses, setFeePaymentStatuses] = useState<DocumentWithId[]>([]);
  const [selectedFeePaymentStatuses, setSelectedFeePaymentStatuses] = useState<string>('');
  const [fileSubmissionStatuses, setFileSubmissionStatuses] = useState<DocumentWithId[]>([]);
  const [registrationFeeStatuses, setRegistrationFeeStatuses] = useState<DocumentWithId[]>([]);
  const [selectedFileSubmissionStatuses, setSelectedFileSubmissionStatuses] = useState<string>('');
  const [selectedRegistrationFeeStatuses, setSelectedRegistrationFeeStatuses] = useState<string>('');
  const [campaigns, setCampaigns] = useState<DocumentWithId[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<string>('');
  const [prefferedStreams, setPrefferedStreams] = useState<DocumentWithId[]>([]);
  const [selectedPrefferedStreams, setSelectedPrefferedStreams] = useState<string>('');
  const [programTypes, setProgramTypes] = useState<DocumentWithId[]>([]);
  const [selectedProgramTypes, setSelectedProgramTypes] = useState<string>('');
  const [departments, setDepartments] = useState<DocumentWithId[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string>('');
  const [subAgentDepartments, setSubAgentDepartments] = useState<DocumentWithId[]>([]);
  const [selectedSubAgentDepartments, setSelectedSubAgentDepartments] = useState<string>('');
  const [superAgents, setSuperAgents] = useState<DocumentWithId[]>([]);
  const [selectedSuperAgents, setSelectedSuperAgents] = useState<string>('');
  const [pipelines, setPipelines] = useState<DocumentWithId[]>([]);
  const [selectedPipelines, setSelectedPipelines] = useState<string>(''); 
  const [filterButtons, setFilterButtons] = useState<DocumentWithId[]>([]);
  const [selectedFilterButtons, setSelectedFilterButtons] = useState<string>('');
  const [filterSettings, setFilterSettings] = useState<DocumentWithId[]>([]);
  const [documentCategories, setDocumentCategories] = useState<DocumentWithId[]>([]);
  const [selectedDocumentCategories, setSelectedDocumentCategories] = useState<string>('');
  const [languages, setLanguages] = useState<DocumentWithId[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false); 
  const [activeTab, setActiveTab] = useState('lead');
  const [searchQuery, setSearchQuery] = useState('');

  // Mock filter settings as fallback
  const mockFilterSettings = [
    { id: 'mock_1', data: { title: 'Lead Source Filter', description: 'Filter leads by their source' } },
    { id: 'mock_2', data: { title: 'Lead Stage Filter', description: 'Filter leads by their current stage' } },
    { id: 'mock_3', data: { title: 'Lead Status Filter', description: 'Filter leads by their status' } },
    { id: 'mock_4', data: { title: 'Date Range Filter', description: 'Filter leads by date range' } },
    { id: 'mock_5', data: { title: 'Assigned User Filter', description: 'Filter leads by assigned user' } }
  ];

  const createSampleFilterSettings = async () => {
    if (!userData?.companyId) {
      setToastMessage('Error: No company ID found');
      setShowToast(true);
      return;
    }
    
    const sampleFilters = [
      { title: 'Lead Source Filter', description: 'Filter leads by their source' },
      { title: 'Lead Stage Filter', description: 'Filter leads by their current stage' },
      { title: 'Lead Status Filter', description: 'Filter leads by their status' },
      { title: 'Date Range Filter', description: 'Filter leads by date range' },
      { title: 'Assigned User Filter', description: 'Filter leads by assigned user' },
      { title: 'Country Filter', description: 'Filter leads by country' },
      { title: 'Program Type Filter', description: 'Filter leads by program type' },
      { title: 'Priority Filter', description: 'Filter leads by priority level' }
    ];

    try {
      console.log('Creating sample filter settings...');
      for (const filter of sampleFilters) {
        const docRef = doc(db, `companies/${userData.companyId}/filterSettings`, filter.title.replace(/\s+/g, '_').toLowerCase());
        await setDoc(docRef, {
          title: filter.title,
          description: filter.description,
          isActive: true,
          createdAt: new Date().toISOString()
        });
        console.log(`Created filter setting: ${filter.title}`);
      }
      console.log('Sample filter settings created successfully');
      // Refresh the data to show the new filter settings
      fetchData();
      setToastMessage('Sample filter settings created successfully!');
      setShowToast(true);
    } catch (error: any) {
      console.error('Error creating sample filter settings:', error);
      if (error?.code === 'permission-denied') {
        setToastMessage('Permission denied: Please check Firestore security rules for filterSettings collection');
      } else {
        setToastMessage(`Error creating filter settings: ${error?.message || 'Unknown error'}`);
      }
      setShowToast(true);
    }
  };


  const fetchData = async () => {
    try {
      if (userData?.companyId) {
        // Fetch admission statuses
        const admissionStatusesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadAdmissionStatus`));
        const leadSubAdmissionStatusesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadAdmissionSubStatus`));
        setAdmissionStatuses(admissionStatusesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as LeadAdmissionStatus
        }))
          .sort((a, b) => {
            return a.data.level - b.data.level;
          })
        );
        setLeadSubAdmissionStatuses(leadSubAdmissionStatusesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as LeadSubAdmissionStatus
        })));

        // Fetch lead sources
        const leadSourcesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadSources`));
        setLeadSources(
          leadSourcesSnapshot.docs
            .map(doc => ({
              id: doc.id,
              data: doc.data() as LeadSource
            }))
            .sort((a, b) => a.data.source.localeCompare(b.data.source))
        );;

        const leadSubSourcesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadSubSources`));
        setLeadSubSources(leadSubSourcesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as LeadSubSource
        }))
          .sort((a, b) => a.data.subSource.localeCompare(b.data.subSource))
        );

        // Fetch lead stages
        const leadStagesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadStages`));
        const leadSubStagesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadSubStages`));
        setLeadStages(leadStagesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as LeadStage
        }))
          .sort((a, b) => {
            return a.data.priority - b.data.priority;
          })
        );
        setLeadSubStages(leadSubStagesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as LeadSubStage
        })));

        // Fetch lead states
        const leadStatesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadStates`));
        setLeadStates(
          leadStatesSnapshot.docs
            .map(doc => ({
              id: doc.id,
              data: doc.data() as LeadState
            }))
            .sort((a, b) => a.data.state.localeCompare(b.data.state))
        );

        // Fetch lead statuses
        const leadStatusesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadStatuses`));
        const leadSubStatusesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadSubStatuses`));
        setLeadStatuses(leadStatusesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as LeadStatus
        }))
          .sort((a, b) => {
            return a.data.priority - b.data.priority;
          })
        );
        setLeadSubStatuses(leadSubStatusesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as LeadSubStatus
        })));

        // Fetch welcome call statuses
        const welcomeCallByAdmissionTeamStatusesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadWelcomeCallByAdmissionTeamStatus`));
        setWelcomeCallByAdmissionTeamStatuses(welcomeCallByAdmissionTeamStatusesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as LeadWelcomeCallByAdmissionTeamStatus
        }))
          .sort((a, b) => a.data.welcomeCallByAdmissionTeamStatus.localeCompare(b.data.welcomeCallByAdmissionTeamStatus))
        );

        // Fetch visa processing statuses
        const visaProcessingStatusesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadVisaFileProcessingStatus`));
        setVisaProcessingStatuses(visaProcessingStatusesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as LeadVisaFileProcessingStatus
        }))
          .sort((a, b) => a.data.visaFileProcessingStatus.localeCompare(b.data.visaFileProcessingStatus))
        );

        // Fetch application moved statuses
        const applicationMovedStatusesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadApplicationMovedStatus`));
        setApplicationMovedStatuses(applicationMovedStatusesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as LeadApplicationMovedStatus
        }))
          .sort((a, b) => a.data.applicationMovedStatus.localeCompare(b.data.applicationMovedStatus))
        );

        // Fetch offer letter statuses
        const offerLetterStatusesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadOfferLetterStatus`));
        setOfferLetterStatuses(offerLetterStatusesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as LeadOfferLetterStatus
        }))
          .sort((a, b) => a.data.offerLetterStatus.localeCompare(b.data.offerLetterStatus))
        );

        // Fetch fee payment statuses
        const feePaymentStatusesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadFeePaymentStatus`));
        setFeePaymentStatuses(feePaymentStatusesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as LeadFeePaymentStatus
        }))
          .sort((a, b) => a.data.feePaymentStatus.localeCompare(b.data.feePaymentStatus))
        );

        // Fetch file submission statuses
        const fileSubmissionStatusesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadFileSubmissionStatus`));
        setFileSubmissionStatuses(fileSubmissionStatusesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as LeadFileSubmissionStatus
        }))
          .sort((a, b) => a.data.fileSubmissionStatus.localeCompare(b.data.fileSubmissionStatus))
        );

        // Fetch registration fee
        const registrationFeeStatusesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/registrationFeeDetails`));
        setRegistrationFeeStatuses(registrationFeeStatusesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as RegistrationFeeDetails
        })));

        const companyCampaignsSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadCampaigns`));
        setCampaigns(companyCampaignsSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as LeadCampaigns
        }))
          .sort((a, b) => a.data.campaign.localeCompare(b.data.campaign))
        );

        const prefferedStreamsSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/preferredStreams`));
        setPrefferedStreams(prefferedStreamsSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as PrefferedStreams
        }))
          .sort((a, b) => a.data.preferredStream.localeCompare(b.data.preferredStream))
        );

        const programTypesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/programTypes`));
        setProgramTypes(programTypesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as ProgramTypes
        }))
          .sort((a, b) => a.data.programType.localeCompare(b.data.programType))
        );

        const companyDepartmentsSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/departments`));
        setDepartments(
          companyDepartmentsSnapshot.docs
            .map(doc => ({
              id: doc.id,
              data: doc.data()
            }))
            .sort((a, b) => a.data.department.localeCompare(b.data.department))
        );

        const subAgentDepartmentsSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/subAgentPermissions`));
        setSubAgentDepartments(subAgentDepartmentsSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data()
        }))
          .sort((a, b) => a.data.department.localeCompare(b.data.department))
        );

        const superAgentsSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/superAgents`));
        setSuperAgents(superAgentsSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data()
        }))
          .sort((a, b) => a.data.superAgent.localeCompare(b.data.superAgent))
        );

        const pipelinesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadPipelines`));
        setPipelines(pipelinesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as Pipeline
        })));

        const filterButtonsSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/filterButtons`));
        setFilterButtons(filterButtonsSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as FilterButton
        }))
          .sort((a, b) => a.data.name.localeCompare(b.data.name))
        );

        // Fetch filter settings
        try {
          const filterSettingsSnapshot = await getDocs(collection(db, `filterSettings`));
          const filterSettingsData = filterSettingsSnapshot.docs.map(doc => ({
            id: doc.id,
            data: doc.data() as FilterSetting
          }))
            .filter(item => {
              // Filter out specific items: Admission Sub Status, Sub Sources, Sub Statuses, and Sub Stages
              const title = item.data.title.toLowerCase();
              return !title.includes('admission sub status') && 
                     !title.includes('sub sources') && 
                     !title.includes('sub statuses') && 
                     !title.includes('sub stages');
            })
            .sort((a, b) => a.data.title.localeCompare(b.data.title));
          
          console.log('Fetched filter settings:', filterSettingsData);
          
          // If no real data exists, use mock data
          if (filterSettingsData.length === 0) {
            console.log('No filter settings found, using mock data');
            setFilterSettings(mockFilterSettings);
          } else {
            setFilterSettings(filterSettingsData);
          }
        } catch (filterError) {
          console.warn('Could not fetch filter settings, using mock data:', filterError);
          setFilterSettings(mockFilterSettings); // Use mock data as fallback
        }

        // Fetch document categories
        const documentCategoriesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadDocumentCategories`));
        setDocumentCategories(documentCategoriesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as DocumentCategory
        }))
          .sort((a, b) => a.data.categoryName.localeCompare(b.data.categoryName))
        );

        // Fetch languages
        const languagesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/language`));
        setLanguages(languagesSnapshot.docs.map(doc => ({
          id: doc.id,
          data: doc.data() as Language
        }))
          .sort((a, b) => a.data.language.localeCompare(b.data.language))
        );
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userData?.companyId]);

  const handleAddSettings = (title: string) => {
    setAction('add');
    setTitle(title);
    setShowModal(true);
  };

  const handleEditSettings = (title: string, data: DocumentWithId[]) => {

    console.log('Editing settings with title:', title, 'and data:', data);

    setAction('edit');
    setTitle(title);
    setData(data);
    setShowModal(true);

    // switch case according to title
    switch (title) {
      case 'Lead Sources':
        // set data with lead sub sources if available
        if (leadSubSources.length > 0) {
          setData(data.map(item => ({
            ...item,
            sub: leadSubSources.filter(sub => sub.data.sourceId === item.id).sort((a, b) => a.data.subSource.localeCompare(b.data.subSource))
          })));
        }
        break;
      case 'Lead Stages':
        // set data with lead sub stages if available
        if (leadSubStages.length > 0) {
          setData(data.map(item => ({
            ...item,
            sub: leadSubStages.filter(sub => sub.data.stageId === item.id).sort((a, b) => a.data.subStage.localeCompare(b.data.subStage))
          })));
        }
        break;
      case 'Admission Status':
        // set data with lead sub admission statuses if available
        if (leadSubAdmissionStatuses.length > 0) {
          setData(data.map(item => ({
            ...item,
            sub: leadSubAdmissionStatuses.filter(sub => sub.data.admissionStatusId === item.id).sort((a, b) => a.data.subAdmissionStatus.localeCompare(b.data.subAdmissionStatus))
          })));
        }
        break;
      case 'Lead Status':
        // set data with lead sub statuses if available
        if (leadSubStatuses.length > 0) {
          setData(data.map(item => ({
            ...item,
            sub: leadSubStatuses.filter(sub => sub.data.statusId === item.id).sort((a, b) => a.data.subStatus.localeCompare(b.data.subStatus))
          })));
        }
        break;
      case 'Document Categories':
        // Document categories don't have sub-items in the same way
        // They have document values within each category
        break;
      default:
        console.error('Unknown title:', title);
    }
  };

  const handleModalClose = (message: string | undefined) => {
    if (message) {
      setToastMessage(message);
      setShowToast(true);
    }
    setShowModal(false);
    setDeleteConfirmationModal(false);
    setDeleting(false);
    setConfirmDelete(false);
    setSelectedAdmissionStatuses('');
    setSelectedLeadSources('');
    setSelectedLeadStages('');
    setSelectedLeadStates('');
    setSelectedLeadStatuses('');
    setSelectedWelcomeCallByAdmissionTeamStatuses('');
    setSelectedVisaProcessingStatuses('');
    setSelectedApplicationMovedStatuses('');
    setSelectedOfferLetterStatuses('');
    setSelectedFeePaymentStatuses('');
    setSelectedFileSubmissionStatuses('');
    setSelectedRegistrationFeeStatuses('');
    setSelectedCampaigns('');
    setSelectedPrefferedStreams('');
    setSelectedProgramTypes('');
    setSelectedDepartments('');
    setSelectedPipelines('');
    setSelectedFilterButtons('');
    setSelectedDocumentCategories('');
    setSelectedLanguages('');
    fetchData();
  }

  useEffect(() => {
    const deleteSettings = async () => {
      if (!userData?.companyId) {
        console.error('No company ID found');
        return;
      }

      setIsSaving(true);
      try {
        // Map of selected items to their corresponding paths
        const pathMap = {
          selectedAdmissionStatuses: 'leadAdmissionStatus',
          selectedLeadSources: 'leadSources',
          selectedLeadStages: 'leadStages',
          selectedLeadStates: 'leadStates',
          selectedLeadStatuses: 'leadStatuses',
          selectedWelcomeCallByAdmissionTeamStatuses: 'leadWelcomeCallByAdmissionTeamStatus',
          selectedVisaProcessingStatuses: 'leadVisaFileProcessingStatus',
          selectedApplicationMovedStatuses: 'leadApplicationMovedStatus',
          selectedOfferLetterStatuses: 'leadOfferLetterStatus',
          selectedFeePaymentStatuses: 'leadFeePaymentStatus',
          selectedFileSubmissionStatuses: 'leadFileSubmissionStatus',
          selectedRegistrationFeeStatuses: 'registrationFeeDetails',
          selectedCampaigns: 'leadCampaigns',
          selectedPrefferedStreams: 'preferredStreams',
          selectedProgramTypes: 'programTypes',
          selectedDepartments: 'departments',
          selectedSubAgentDepartments: 'subAgentPermissions',
          selectedSuperAgents: 'superAgents',
          selectedPipelines: 'leadPipelines',
          selectedFilterButtons: 'filterButtons',
          selectedDocumentCategories: 'leadDocumentCategories',
          selectedLanguages: 'language'
        };

        // Process all deletions
        const deletionPromises = Object.entries(pathMap)
          .filter(([selectedVar, _]) => {
            const value = eval(selectedVar);
            // Check that value exists and is not an empty string
            return value && value.trim() !== '';
          })
          .map(([selectedVar, collectionPath]) => {
            const selectedValue = eval(selectedVar);
            return deleteDoc(doc(db, `companies/${userData.companyId}/${collectionPath}/${selectedValue}`));
          });

        if (deletionPromises.length > 0) {
          await Promise.all(deletionPromises);
          setDeleting(false);
          handleModalClose(undefined);
          setToastMessage('Settings deleted successfully!');
          setShowToast(true);
        }
      } catch (error) {
        console.error('Error deleting settings:', error);
      } finally {
        setIsSaving(false);
      }
    };

    // Check if any valid selection exists (not empty string)
    const hasValidSelection = [
      selectedAdmissionStatuses,
      selectedLeadSources,
      selectedLeadStages,
      selectedLeadStates,
      selectedLeadStatuses,
      selectedWelcomeCallByAdmissionTeamStatuses,
      selectedVisaProcessingStatuses,
      selectedApplicationMovedStatuses,
      selectedOfferLetterStatuses,
      selectedFeePaymentStatuses,
      selectedFileSubmissionStatuses,
      selectedRegistrationFeeStatuses,
      selectedCampaigns,
      selectedPrefferedStreams,
      selectedProgramTypes,
      selectedDepartments,
      selectedSubAgentDepartments,
      selectedSuperAgents,
      selectedPipelines,
      selectedFilterButtons,
      selectedDocumentCategories,
      selectedLanguages
    ].some(value => value && value.trim() !== '');

    if (hasValidSelection && !confirmDelete) {
      setDeleteConfirmationModal(true);
    } else if (confirmDelete) {
      setDeleting(true);
      deleteSettings();
    }
  }, [
    userData?.companyId,
    confirmDelete,
    selectedAdmissionStatuses,
    selectedLeadSources,
    selectedLeadStages,
    selectedLeadStates,
    selectedLeadStatuses,
    selectedWelcomeCallByAdmissionTeamStatuses,
    selectedVisaProcessingStatuses,
    selectedApplicationMovedStatuses,
    selectedOfferLetterStatuses,
    selectedFeePaymentStatuses,
    selectedFileSubmissionStatuses,
    selectedRegistrationFeeStatuses,
    selectedCampaigns,
    selectedPrefferedStreams,
    selectedProgramTypes,
    selectedDepartments,
    selectedSubAgentDepartments,
    selectedSuperAgents,
    selectedPipelines,
    selectedFilterButtons,
    selectedDocumentCategories,
    selectedLanguages
  ]);

  const filterItems = (items: DocumentWithId[], getDisplayText: (item: any) => string) => {
    if (!searchQuery) return items;
    return items.filter(item =>
      getDisplayText(item.data).toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const renderSettingsBox = (
    title: string,
    items: DocumentWithId[],
    // selectedItems: string[],
    setSelectedItems: React.Dispatch<React.SetStateAction<string>>,
    getDisplayText: (item: any) => string,
    getColor?: (item: any) => string | undefined,
    getLevel?: (item: any) => number
  ) => (
    <div className="flex flex-col bg-white rounded-lg shadow-sm">
      <div className="bg-gray-50 px-4 py-2 rounded-t-lg">
        <h3 className="text-sm font-semibold font-Montserrat text-gray-700">
          {title}
        </h3>
      </div>
      <div className="bg-white p-3 rounded-b-lg h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        <div className="space-y-2">
          <div
            onClick={() => handleAddSettings(title)}
            className="flex justify-between items-center hover:bg-gray-50 p-1 rounded transition-colors"
          >
            <span className="text-sm font-Montserrat text-gray-600">Add {title}</span>
            <button className='flex items-center gap-2 rounded bg-green-200 hover:bg-green-300 p-1'>
              <div className='text-sm font-medium'>
                Add
              </div>
              <svg
                className="w-4 h-4 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </button>
          </div>

          {filterItems(items, getDisplayText).map((item) => (
            <div
              key={item.id}
              className="flex justify-between items-center hover:bg-gray-50 p-1 rounded transition-colors"
              title={getDisplayText(item.data)}
            >
              <span
                className="text-sm font-Montserrat text-gray-600 truncate"
                style={getColor ? { color: getColor(item.data) } : undefined}
              >
                {getDisplayText(item.data)}
              </span>

              <div className='flex items-center gap-2'>
                <span className="text-sm font-Montserrat text-gray-400">
                  {getLevel ? `(${getLevel(item.data)})` : undefined}
                </span>
                <button className="p-1 hover:bg-gray-100 rounded"
                  onClick={() => {
                    handleEditSettings(title, [item]);
                  }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/desktop/editicon.svg`}
                    alt="Edit Icon"
                    className="w-5 h-5 cursor-pointer"
                  />
                </button>
                <button className="p-1 hover:bg-gray-100 rounded"
                  onClick={() => {
                    setSelectedItems(item.id);
                  }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                    alt={"Delete Icon"}
                    className="w-4 h-4 cursor-pointer"
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSettingsCheckBox = (
    title: string,
    items: DocumentWithId[],
    // selectedItems: string[],
    setSelectedItems: React.Dispatch<React.SetStateAction<string>>,
    getDisplayText: (item: any) => string,
    getColor?: (item: any) => string | undefined,
    getLevel?: (item: any) => number
  ) => (
    <div className="flex flex-col bg-white rounded-lg shadow-sm">
      <div className="bg-gray-50 px-4 py-2 rounded-t-lg">
        <h3 className="text-sm font-semibold font-Montserrat text-gray-700">
          {title}
        </h3>
      </div>
      <div className="bg-white p-3 rounded-b-lg h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        <div className="space-y-2">
          {filterItems(items, getDisplayText).map((item) => (
            <div
              key={item.id}
              className="flex justify-between items-center hover:bg-gray-50 p-1 rounded transition-colors"
              title={getDisplayText(item.data)}
            >
              <span
                className="text-sm font-Montserrat text-gray-600 truncate"
                style={getColor ? { color: getColor(item.data) } : undefined}
              >
                {getDisplayText(item.data)}
              </span>

              <div className='flex items-center gap-2'>
                {/* check box */}
                <input
                  type="checkbox"
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  checked={false}
                  onChange={() => {
                    // handle checkbox change
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSettingsCardBox = (
    title: string,
    items: DocumentWithId[],
    // selectedItems: string[],
    setSelectedItems: React.Dispatch<React.SetStateAction<string>>,
    getDisplayText: (item: any) => string,
    getDiscription?: (item: any) => string,
  ) => {
    const cardAnimation = "transform transition-transform duration-300 hover:scale-95 cursor-pointer";
    return (
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4'>
        <div className={`flex items-center w-full p-6 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl shadow-xl hover:bg-white/20 ${cardAnimation} hover:shadow-2xl hover:border-white/30`}
          onClick={() => handleAddSettings(title)}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 backdrop-blur-sm border border-black/30 rounded-lg flex items-center justify-center hover:bg-black/30 transition-colors duration-200">
              <Plus className="w-4 h-4 text-black" />
            </div>
            <h5 className="text-2xl font-bold tracking-tight text-black">Add {title}</h5>
          </div>
        </div>
        {filterItems(items, getDisplayText).map((item) => (
          <div
            key={item.id}
            className={`relative w-full p-6 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl shadow-xl hover:bg-white/20 ${cardAnimation} hover:shadow-2xl hover:border-white/30`}
            title={getDisplayText(item.data)}
            onClick={() => handleEditSettings(title, [item])}
          >
            {/* Delete button in top-right corner */}
            <div className="absolute top-3 right-3">
              <button 
                className="p-1 hover:bg-white/30 rounded transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedItems(item.id);
                }}
                title="Delete"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                  alt="Delete Icon"
                  className="w-4 h-4"
                />
              </button>
            </div>
            
            {/* Title */}
            <div className="flex items-center gap-3 mb-2 pr-8">
              <h5 className="text-2xl font-bold tracking-tight text-black">{getDisplayText(item.data)}</h5>
            </div>
            {/* Description */}
            <p className="font-normal text-black/80 leading-relaxed pr-8">
              {getDiscription ? getDiscription(item.data) : 'No description available.'}
            </p>
          </div>
        ))}
      </div>
      // <p className="flex items-center justify-center text-lg font-semibold text-gray-700 mb-4">
      //   Pipeline under development
      // </p>
    );
  };

  const tabs = [
    { id: 'lead', label: 'Lead Management', icon: 'user-group' },
    { id: 'employee', label: 'Staff Management', icon: 'user' },
    { id: 'subAgent', label: 'Affiliate Management', icon: 'user' },
    // { id: 'information', label: 'Information Management', icon: 'information-circle' },
    { id: 'admission', label: 'Admission Process', icon: 'document-text' },
    { id: 'application', label: 'Application Process', icon: 'document-text' },
    { id: 'payment', label: 'Payment & Documents', icon: 'credit-card' },
    { id: 'campaign', label: 'Campaigns', icon: 'mail' },
    { id: 'pipeline', label: 'Pipeline Management', icon: 'pipeline-circle' },
    { id: 'filters', label: 'Filter Buttons', icon: 'filter' },
    { id: 'custom', label: 'Custom', icon: 'cog' }
  ];

  const getTabContent = (tabId: string) => {
    switch (tabId) {
      case 'lead':
        return (
          <div className="grid grid-cols-3 gap-4">
            {renderSettingsBox(
              'Lead Sources',
              leadSources,
              // selectedLeadSources,
              setSelectedLeadSources,
              (item: LeadSource) => item.source
            )}
            {renderSettingsBox(
              'Lead Stages',
              leadStages,
              // selectedLeadStages,
              setSelectedLeadStages,
              (item: LeadStage) => item.stage,
              (item: LeadStage) => item.color,
              (item: LeadStage) => item.priority
            )}
            {renderSettingsBox(
              'Lead States',
              leadStates,
              // selectedLeadStates,
              setSelectedLeadStates,
              (item: LeadState) => item.state,
              (item: LeadStage) => item.color
            )}
            {renderSettingsBox(
              'Lead Status',
              leadStatuses,
              // selectedLeadStatuses,
              setSelectedLeadStatuses,
              (item: LeadStatus) => item.status,
              (item: LeadStatus) => item.color,
              (item: LeadStatus) => item.priority
            )}
            {renderSettingsBox(
              'Admission Status',
              admissionStatuses,
              // selectedAdmissionStatuses,
              setSelectedAdmissionStatuses,
              (item: LeadAdmissionStatus) => item.admissionStatus,
              undefined,
              (item: LeadAdmissionStatus) => item.level
            )}
          </div>
        );
      case 'employee':
        return (
          <div className="grid grid-cols-3 gap-4">
            {renderSettingsBox(
              'Departments',
              departments,
              // selectedDepartments,
              setSelectedDepartments,
              (item: Department) => item.department
            )}
          </div>
        );
      case 'subAgent':
        return (
          <div className="grid grid-cols-3 gap-4">
            {renderSettingsBox(
              'Affiliate Departments',
              subAgentDepartments,
              // selectedSubAgentDepartments,
              setSelectedSubAgentDepartments,
              (item: Department) => item.department
            )}
          </div>
        );
      case 'information':
        return (
          <div className="grid grid-cols-3 gap-4">
            {renderSettingsCheckBox(
              'Lead Management (Table)',
              departments,
              // selectedDepartments,
              setSelectedDepartments,
              (item: Department) => item.department
            )}
          </div>
        );
      case 'admission':
        return (
          <div className="grid grid-cols-3 gap-4">
            {renderSettingsBox(
              'Preffered Stream',
              prefferedStreams,
              // selectedPrefferedStreams,
              setSelectedPrefferedStreams,
              (item: PrefferedStreams) => item.preferredStream
            )}
            {renderSettingsBox(
              'Program Type',
              programTypes,
              // selectedProgramTypes,
              setSelectedProgramTypes,
              (item: ProgramTypes) => item.programType
            )}
            {renderSettingsBox(
              'Super Agent',
              superAgents,
              // selectedProgramTypes,
              setSelectedSuperAgents,
              (item: SuperAgent) => item.superAgent
            )}
          </div>
        );
      case 'application':
        return (
          <div className="grid grid-cols-3 gap-4">
            {renderSettingsBox(
              'Welcome Call Status',
              welcomeCallByAdmissionTeamStatuses,
              // selectedWelcomeCallByAdmissionTeamStatuses,
              setSelectedWelcomeCallByAdmissionTeamStatuses,
              (item: LeadWelcomeCallByAdmissionTeamStatus) => item.welcomeCallByAdmissionTeamStatus
            )}
            {renderSettingsBox(
              'Visa Processing',
              visaProcessingStatuses,
              // selectedVisaProcessingStatuses,
              setSelectedVisaProcessingStatuses,
              (item: LeadVisaFileProcessingStatus) => item.visaFileProcessingStatus
            )}
            {renderSettingsBox(
              'Application Status',
              applicationMovedStatuses,
              // selectedApplicationMovedStatuses,
              setSelectedApplicationMovedStatuses,
              (item: LeadApplicationMovedStatus) => item.applicationMovedStatus
            )}
            {renderSettingsBox(
              'Offer Letter Status',
              offerLetterStatuses,
              // selectedOfferLetterStatuses,
              setSelectedOfferLetterStatuses,
              (item: LeadOfferLetterStatus) => item.offerLetterStatus
            )}
          </div>
        );
      case 'payment':
        return (
          <div className="grid grid-cols-3 gap-4">
            {renderSettingsBox(
              'Fee Payment Status',
              feePaymentStatuses,
              // selectedFeePaymentStatuses,
              setSelectedFeePaymentStatuses,
              (item: LeadFeePaymentStatus) => item.feePaymentStatus
            )}
            {renderSettingsBox(
              'File Submission Status',
              fileSubmissionStatuses,
              // selectedFileSubmissionStatuses,
              setSelectedFileSubmissionStatuses,
              (item: LeadFileSubmissionStatus) => item.fileSubmissionStatus
            )}
            {renderSettingsBox(
              'Registration fees',
              registrationFeeStatuses,
              setSelectedRegistrationFeeStatuses,
              (item: RegistrationFeeDetails) => `${item.registrationFeeName} - ${item.registrationFeeAmount}`,
              undefined,
              undefined
            )}
            {renderSettingsBox(
              'Document Categories',
              documentCategories,
              setSelectedDocumentCategories,
              (item: DocumentCategory) => `${item.categoryName} (${item.documentValues?.length || 0} docs)`,
              undefined,
              undefined
            )}
          </div>
        );
      case 'campaign':
        return (
          <div className="grid grid-cols-3 gap-4">
            {renderSettingsBox(
              'Campaigns',
              campaigns,
              // selectedCampaigns,
              setSelectedCampaigns,
              (item: LeadCampaigns) => item.campaign
            )}
          </div>
        );
      case 'pipeline':
        return (
          <div>
            {renderSettingsCardBox(
              'Pipeline',
              pipelines,
              // selectedSubAgentDepartments,
              setSelectedPipelines,
              (item: Pipeline) => item.pipelineName,
              (item: Pipeline) => item.pipelineDescription
            )}
          </div>
        );
      case 'filters':
        return (
          <div>
            {renderSettingsCardBox(
              'Filter Button',
              filterButtons,
              // selectedSubAgentDepartments,
              setSelectedFilterButtons,
              (item: FilterButton) => item.name,
              (item: FilterButton) => item.description
            )}
          </div>
        );
      case 'custom':
        return (
          <div className="grid grid-cols-3 gap-4">
            {renderSettingsBox(
              'Languages',
              languages,
              setSelectedLanguages,
              (item: Language) => item.language
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-200">
      <div className="fixed top-16 left-0 h-full z-10">
        <SideNavbar />
      </div>

      <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
        <div className="bg-white p-6 rounded-3xl w-full">
          <div className="flex items-center gap-6 mb-6">
            <div
              className="flex items-center justify-center w-14 h-14 rounded-full"
              style={{ background: 'linear-gradient(180deg, #00A6D9 10%, #3483BE 100%)' }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/desktop/sidebaricon/settings-svgrepo-white.svg`}
                alt="Settings"
                className="w-8 h-8"
              />
            </div>
            <div className="flex-1">
              <h2 className="font-montserrat text-2xl font-medium text-black">Settings</h2>
              <p className="text-sm text-gray-500 mt-1">Manage your application settings and preferences</p>
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="Search settings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 px-3 py-1.5 text-sm border rounded-lg pl-8 focus:outline-none focus:ring-1 focus:ring-cyan-500"
              />
              <svg
                className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>

          <div className="flex space-x-1 mb-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-4 py-2 text-sm font-medium rounded-lg transition-colors
                  ${activeTab === tab.id
                    ? 'bg-gradient-to-r from-[#3483BE] to-[#02385E] text-white'
                    : 'text-gray-500 hover:bg-gray-50'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="mt-4 mb-8">
            {getTabContent(activeTab)}
          </div>

          {/* <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`
                inline-flex items-center px-6 py-2 text-sm font-medium rounded-3xl
                ${isSaving
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#3483BE] to-[#02385E] hover:opacity-90 text-white'
                }
                transition-colors duration-200
              `}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving Changes...
                </>
              ) : (
                'Save Settings'
              )}
            </button>
          </div> */}
        </div>
      </div>
      {showModal && (
        <SettingsModal title={title} onClose={handleModalClose} action={action} data={data} filterSettings={filterSettings} />
      )}

      {deleteConfirmationModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-3xl w-[90%] md:w-[70%] lg:w-[50%] relative ">
            <div className="mt-4">
              <p className="text-sm text-gray-700">Are you sure you want to delete this setting?</p>
              <p className="text-sm text-red-500">Note: Deleting this will lead to losing of this status connected to your lead</p>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => handleModalClose(undefined)}
                className="px-4 py-2 text-sm font-medium text-gray-500 border rounded-3xl mr-4"
              >
                Cancel
              </button>
              <button
                onClick={() => setConfirmDelete(true)}
                className="px-8 py-2 bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] text-white rounded-3xl flex items-center justify-center"
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showToast && (
        <Toast
          message={toastMessage}
          isError={
            toastMessage.includes('Error') ||
            toastMessage.includes('Cannot') ||
            toastMessage.includes('Failed')
          }
          show={showToast}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
};

export default Settings;